// src/components/admin/AdminUsers.js

import React, { useState, useEffect, useMemo } from 'react';
import { 
  collection, getDocs, doc, updateDoc, query, runTransaction, writeBatch, increment, setDoc, getDoc, addDoc, collectionGroup 
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { COLORS } from '../../theme';
import { 
  formatDateToIso, formatCurrency, getUserRank, getUserRankIds 
} from '../../utils';

// ★ Functions の呼び出しヘルパー（import はすべてファイル先頭にまとめる必要がある）
const callFn = (name) => httpsCallable(getFunctions(getApp(), 'asia-northeast1'), name);

// --- Sub Components (Modal) ---

const ProfileModal = ({ user, onClose, pointUnit = 'RB', refreshData, ranks }) => {
    if (!user) return null;
    const userRank = getUserRank(user, ranks);

    const calculateAge = (birthDateString) => {
        if (!birthDateString) return '';
        const today = new Date();
        const birthDate = new Date(birthDateString);
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return ` (${age}歳)`;
    };

    const handleEditTotalPaid = async () => {
        const { value } = await Swal.fire({
            title: '累計支払額の修正',
            text: 'ランキング集計用の数値を直接修正します',
            input: 'number',
            inputValue: user.totalPaid || 0,
            showCancelButton: true,
            confirmButtonText: '修正保存',
            didOpen: () => { const swalContainer = Swal.getContainer(); if(swalContainer) swalContainer.style.zIndex = '99999'; }
        });

        if (value !== null && value !== undefined) {
            try {
                await updateDoc(doc(db, 'users', user.id), { totalPaid: Number(value) });
                toast.success('修正しました');
                if(refreshData) refreshData(); onClose();
            } catch(e) { toast.error('エラーが発生しました'); }
        }
    };

    const handleCopyLine = (lineId) => {
        if (!lineId) return;
        navigator.clipboard.writeText(lineId);
        toast.success(`LINE ID「${lineId}」をコピーしました！`);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e=>e.stopPropagation()} style={{paddingBottom:'80px'}}>
                <button onClick={onClose} style={{position:'absolute', top:'10px', right:'10px', background:'#f5f5f5', border:'none', borderRadius:'50%', width:'40px', height:'40px', cursor:'pointer', fontWeight:'bold', fontSize:'18px', zIndex:10}}>×</button>
                
                <div style={{textAlign:'center', marginBottom:'20px', marginTop:'10px'}}>
                    {user.pictureUrl ? <img src={user.pictureUrl} style={{width:'80px', height:'80px', borderRadius:'50%', objectFit:'cover', border:'3px solid #eee'}} alt="" /> : <div style={{width:'80px', height:'80px', borderRadius:'50%', background:'#eee', margin:'0 auto', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'30px'}}>👤</div>}
                    <h3 style={{margin:'10px 0 5px 0'}}>
                        {user.displayName}
                        <span style={{fontSize: '16px', marginLeft: '5px'}}>
                            {user.notificationsEnabled && user.fcmToken ? '🔔' : '🔕'}
                        </span>
                    </h3>
                    <div style={{fontSize:'12px', color:'#666', marginBottom:'8px'}}>
                        <span style={{background:'#eee', padding:'4px 10px', borderRadius:'15px', fontWeight:'bold'}}>適用中: {userRank.icon} {userRank.name}</span>
                    </div>
                    <div style={{fontSize:'12px', color:'#666'}}>
                        {user.status === 'approved' ? '✅ 承認済' : (user.status === 'banned' ? `⛔ BAN中 ${user.banUntil ? `(~${new Date(user.banUntil).toLocaleDateString()})` : '(無期限)'}` : '⏳ 承認待ち')}
                    </div>
                    
                    <div style={{marginTop:'15px', fontSize:'24px', fontWeight:'bold', color:'#e65100'}}>
                        <img src="/rbcoin.png" style={{width:'24px', height:'24px', verticalAlign:'middle', marginRight:'5px'}} alt="coin" /> {formatCurrency(user.points || 0)} {pointUnit}
                    </div>
                    
                    <div style={{fontSize:'12px', color:'#666', marginTop:'10px', display:'flex', alignItems:'center', justifyContent:'center', gap:'5px'}}>
                        累計支払: ¥{formatCurrency(user.totalPaid || 0)}
                        <button onClick={handleEditTotalPaid} style={{border:'1px solid #ccc', background:'white', borderRadius:'4px', fontSize:'10px', cursor:'pointer', padding:'2px 8px'}}>✏️修正</button>
                    </div>
                </div>

                <div style={{background:'#e3f2fd', padding:'15px', borderRadius:'12px', marginBottom:'15px'}}>
                    <div style={{fontSize:'12px', fontWeight:'bold', marginBottom:'5px', color:COLORS.primary}}>📞 連絡先・ID</div>
                    <div style={{fontSize:'12px', marginBottom:'3px'}}>📱 {user.phoneNumber || '-'}</div>
                    <div style={{fontSize:'12px', marginBottom:'3px'}}>📧 {user.email || '-'}</div>
                    
                    <div style={{fontSize:'12px', marginBottom:'3px', display:'flex', alignItems:'center', gap:'5px'}}>
                        💬 LINE ID: <b>{user.lineId || '-'}</b>
                        {user.lineId && (
                            <button 
                                onClick={() => handleCopyLine(user.lineId)} 
                                style={{background:'#06c755', color:'white', border:'none', borderRadius:'4px', padding:'2px 8px', fontSize:'10px', cursor:'pointer', fontWeight:'bold'}}
                            >
                                コピー
                            </button>
                        )}
                    </div>
                    
                    <div style={{fontSize:'12px'}}>🅿️ PayPay: <b>{user.paypayId || '-'}</b></div>
                    <div style={{fontSize:'12px', marginTop:'8px', borderTop:'1px dashed #ccc', paddingTop:'8px', color:'#555'}}>
                        🕒 最終ログイン: {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : '未ログイン'}
                    </div>
                </div>

                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'15px'}}>
                    <div style={{background:'#f9f9f9', padding:'10px', borderRadius:'8px'}}>
                        <div style={{fontSize:'10px', color:'#999'}}>生年月日 (年齢)</div>
                        <div style={{fontWeight:'bold'}}>{user.birthDate ? `${user.birthDate}${calculateAge(user.birthDate)}` : '-'}</div>
                    </div>
                    <div style={{background:'#f9f9f9', padding:'10px', borderRadius:'8px'}}>
                        <div style={{fontSize:'10px', color:'#999'}}>性別</div>
                        <div style={{fontWeight:'bold'}}>{user.gender || '未設定'}</div>
                    </div>
                </div>

                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'15px'}}>
                    <div style={{background:'#f9f9f9', padding:'10px', borderRadius:'8px'}}>
                        <div style={{fontSize:'10px', color:'#999'}}>背番号</div>
                        <div style={{fontWeight:'bold'}}>{user.number || '-'}</div>
                    </div>
                    <div style={{background:'#f9f9f9', padding:'10px', borderRadius:'8px'}}>
                        <div style={{fontSize:'10px', color:'#999'}}>ポジション</div>
                        <div style={{fontWeight:'bold', color: (!user.mainPosition || user.mainPosition === '未設定') ? '#d32f2f' : 'inherit'}}>
                            {(user.mainPosition && user.mainPosition !== '未設定') ? `${user.mainPosition} (${user.subPosition})` : '未設定'}
                        </div>
                    </div>
                </div>

                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'15px'}}>
                    <div style={{background:'#f9f9f9', padding:'10px', borderRadius:'8px'}}>
                        <div style={{fontSize:'10px', color:'#999'}}>利き足</div>
                        <div style={{fontWeight:'bold'}}>{user.dominantFoot || '-'}</div>
                    </div>
                    <div style={{background:'#f9f9f9', padding:'10px', borderRadius:'8px'}}>
                        <div style={{fontSize:'10px', color:'#999'}}>身長 / 体重</div>
                        <div style={{fontWeight:'bold'}}>{user.height ? `${user.height}cm` : '-'} / {user.weight ? `${user.weight}kg` : '-'}</div>
                    </div>
                </div>

                <div style={{background:'#f9f9f9', padding:'15px', borderRadius:'8px', marginBottom:'20px'}}>
                    <div style={{fontSize:'10px', color:'#999', marginBottom:'2px'}}>プレースタイル</div>
                    <div style={{fontSize:'13px', fontWeight:'bold', marginBottom:'12px'}}>{user.playStyle || '-'}</div>

                    <div style={{fontSize:'10px', color:'#999', marginBottom:'2px'}}>好きなチーム / 選手</div>
                    <div style={{fontSize:'13px', fontWeight:'bold', marginBottom:'12px'}}>{user.favoriteTeam || '-'}</div>

                    <div style={{fontSize:'10px', color:'#999', marginBottom:'2px'}}>ひとこと・自己紹介</div>
                    <div style={{fontSize:'13px', whiteSpace:'pre-wrap', lineHeight:'1.5'}}>{user.bio || '-'}</div>
                </div>

                <button onClick={onClose} className="btn" style={{width:'100%', background:'#eee', fontWeight:'bold', padding:'15px'}}>閉じる</button>
            </div>
        </div>
    );
};

const PointModal = ({ user, onClose, onSave, pointUnit = 'RB', actionPresets = [] }) => {
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [linkToAccounting, setLinkToAccounting] = useState(true);
    const [accountingType, setAccountingType] = useState('income');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!user) return null;

    const handlePresetClick = (preset) => {
        setAmount(preset.amount);
        setReason(preset.name);
        if (preset.amount > 0) {
            setAccountingType('expense');
            setLinkToAccounting(true);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!amount || !reason) return alert('金額と理由を入力してください');
        setIsSubmitting(true);
        await onSave(user.id, Number(amount), reason, linkToAccounting, accountingType);
        setIsSubmitting(false);
        onClose();
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e=>e.stopPropagation()}>
                <h3 style={{marginTop:0}}><img src="/rbcoin.png" style={{width:'20px', height:'20px', verticalAlign:'middle'}} alt="coin" /> {pointUnit}操作</h3>
                <p style={{fontSize:'14px'}}>対象: <b>{user.displayName}</b></p>
                
                {actionPresets.length > 0 && (
                    <div style={{marginBottom:'15px', display:'flex', flexWrap:'wrap', gap:'5px', borderBottom:'1px solid #eee', padding:'10px'}}>
                        <div style={{width:'100%', fontSize:'10px', color:'#999', marginBottom:'3px'}}>定型:</div>
                        {actionPresets.map((p, i) => (
                            <button key={i} type="button" onClick={()=>handlePresetClick(p)} style={{fontSize:'11px', padding:'6px 10px', borderRadius:'20px', border:'1px solid #4caf50', background:'#e8f5e9', color:'white'}}>
                                {p.name} ({p.amount > 0 ? '+' : ''}{p.amount})
                            </button>
                        ))}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div style={{marginBottom:'15px'}}>
                        <label style={{fontSize:'12px', display:'block', marginBottom:'5px'}}>増減額</label>
                        <input type="number" value={amount} onChange={e=>setAmount(e.target.value)} placeholder="例: 1000, -500" autoFocus />
                    </div>
                    <div style={{marginBottom:'15px'}}>
                        <label style={{fontSize:'12px', display:'block', marginBottom:'5px'}}>理由</label>
                        <input type="text" value={reason} onChange={e=>setReason(e.target.value)} placeholder="例: チャージ" />
                    </div>
                    
                    {Number(amount) > 0 && (
                        <div style={{marginBottom:'20px', background:'#f5f5f5', padding:'10px', borderRadius:'8px', border:'1px solid #eee'}}>
                            <label style={{fontSize:'13px', display:'flex', alignItems:'center', cursor:'pointer', marginBottom:'10px'}}>
                                <input type="checkbox" checked={linkToAccounting} onChange={e=>setLinkToAccounting(e.target.checked)} style={{marginRight:'8px', width:'auto', marginBottom:0}} />
                                <span style={{fontWeight:'bold'}}>会計に記録</span>
                            </label>
                            {linkToAccounting && (
                                <div style={{paddingLeft:'25px'}}>
                                    <label style={{display:'flex', alignItems:'center', marginBottom:'8px', cursor:'pointer'}}>
                                        <input type="radio" name="accType" checked={accountingType === 'income'} onChange={()=>setAccountingType('income')} style={{width:'auto', marginRight:'8px'}} />
                                        <span style={{color:'#2196F3', fontWeight:'bold'}}>🔵 収入(購入)</span>
                                    </label>
                                    <label style={{display:'flex', alignItems:'center', cursor:'pointer'}}>
                                        <input type="radio" name="accType" checked={accountingType === 'expense'} onChange={()=>setAccountingType('expense')} style={{width:'auto', marginRight:'8px'}} />
                                        <span style={{color:'#f44336', fontWeight:'bold'}}>🔴 支出(報酬/還元)</span>
                                    </label>
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{display:'flex', gap:'10px'}}>
                        <button type="button" onClick={onClose} className="btn" style={{flex:1, background:'#eee'}}>キャンセル</button>
                        <button type="submit" disabled={isSubmitting} className="btn btn-primary" style={{flex:1}}>実行</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Main Component ---

// ★ オーナーのUID。Firestoreルールの isOwner() と必ず揃えること。
//   これはUIの表示制御用であり、セキュリティの実体はルール側にある。
const OWNER_UIDS = [
    'qqk4b0vsx2gh8kYx63wlfOwYZft2',
    'U9775469588b62580bc6a38491f86939c'
];

const AdminUsers = ({ ranks, actionPresets, pointUnit = 'RB' }) => {
    // 運営権限の付与・削除はオーナーのみ。
    // 運営メンバーが操作しても server_secrets を書けず、
    // 「画面上は運営なのに実権限がない」状態になってしまうため。
    // ★ Firestoreルールの request.auth.uid と同じ値を見るので、判定がズレない。
    const isOwnerUser = OWNER_UIDS.includes(auth.currentUser?.uid);
    const [users, setUsers] = useState([]);
    const [viewingUser, setViewingUser] = useState(null);
    const [pointUser, setPointUser] = useState(null);
    const [userTab, setUserTab] = useState('all');
    const [userSearch, setUserSearch] = useState('');
    const [userLimit, setUserLimit] = useState(50);
    const [loading, setLoading] = useState(false);
    
    const [seasonStartDate, setSeasonStartDate] = useState('');

    useEffect(() => {
        fetchUsers();
        fetchConfig();
    }, []);

    const fetchUsers = async () => {
        // 1. 親ドキュメント（全員分）を取得
        const snap = await getDocs(collection(db, "users"));
        // 2. 全員の private サブコレクションを一括取得
        const privatesSnap = await getDocs(collectionGroup(db, 'private'));

        // private データをユーザーIDをキーにした辞書（Map）にする
        const privatesMap = {};
        privatesSnap.forEach(doc => {
            const userId = doc.ref.parent.parent?.id; // users/{userId}/private/profile から userId を抽出
            if (userId) privatesMap[userId] = doc.data();
        });

        // 3. 親データに private データを上書きマージする
        let usersData = snap.docs.map(doc => {
            const parentData = doc.data();
            const privData = privatesMap[doc.id] || {};
            return { 
                id: doc.id, 
                ...parentData,
                // private 側にデータがあればそちらを優先。なければ親のデータ（フォールバック）
                phoneNumber: privData.phoneNumber !== undefined ? privData.phoneNumber : parentData.phoneNumber,
                email: privData.email !== undefined ? privData.email : parentData.email,
                paypayId: privData.paypayId !== undefined ? privData.paypayId : parentData.paypayId,
                birthDate: privData.birthDate !== undefined ? privData.birthDate : parentData.birthDate,
                lineId: privData.lineId !== undefined ? privData.lineId : parentData.lineId,
                transferPassword: privData.transferPassword !== undefined ? privData.transferPassword : parentData.transferPassword,
                // idCooperation は親に残すので parentData のまま
            };
        });

        // ★自動BAN解除システム: 期間を過ぎたBANを全自動で解除する
        const now = new Date();
        let hasUpdates = false;
        const batch = writeBatch(db);
        
        usersData = usersData.map(u => {
            if (u.status === 'banned' && u.banUntil) {
                if (new Date(u.banUntil) <= now) {
                    batch.update(doc(db, 'users', u.id), { status: 'approved', banUntil: null });
                    hasUpdates = true;
                    return { ...u, status: 'approved', banUntil: null };
                }
            }
            return u;
        });
        
        if (hasUpdates) await batch.commit();

        usersData.sort((a, b) => new Date(b.lastLogin || b.createdAt) - new Date(a.lastLogin || a.createdAt));
        setUsers(usersData);
    };

    const fetchConfig = async () => {
        try {
            const docSnap = await getDoc(doc(db, 'settings', 'config'));
            if (docSnap.exists()) {
                setSeasonStartDate(docSnap.data().seasonStartDate || '');
            }
        } catch(e) {
            console.error(e);
        }
    };

    const handleCopyLineId = (lineId) => {
        if (!lineId) return;
        navigator.clipboard.writeText(lineId);
        toast.success(`LINE ID「${lineId}」をコピーしました！`);
    };

    const handleDismissCooperation = async (targetUser) => {
        const result = await Swal.fire({
            title: '確認済みにしますか？',
            html: `<b>${targetUser.displayName}</b> さんをリストから消去します。<br><br><span style="font-size:12px; color:#666;">※ユーザーのプロフィール上の「協力可能」のチェックも外れます。以後はランク付与等で管理してください。</span>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'はい、消去する',
            cancelButtonText: 'キャンセル',
            confirmButtonColor: '#2e7d32'
        });

        if (result.isConfirmed) {
            setLoading(true);
            try {
                await updateDoc(doc(db, 'users', targetUser.id), { idCooperation: false });
                toast.success('リストから消去しました');
                fetchUsers(); 
            } catch (e) {
                console.error(e);
                toast.error('エラーが発生しました');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleResetRanking = async () => {
        const today = formatDateToIso(new Date());
        
        const result = await Swal.fire({
            title: 'ランキングのリセット',
            html: `
                <p style="text-align:left; font-size:14px; margin-bottom:15px;">
                    新しいシーズンを開始し、以下の成績をリセットします。<br>
                    <b style="color:#d32f2f;">・参加数<br>・得点 / アシスト<br>・MOM回数</b>
                    <br><br>
                    <span style="color:#666; font-size:12px;">※「長者番付(コイン)」と「太客(支払額)」はリセットされず、累計で残ります。</span>
                </p>
                <label style="display:block; text-align:left; font-weight:bold;">新シーズンの開始日</label>
            `,
            input: 'date',
            inputValue: today,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: '実行 (リセット)',
            confirmButtonColor: '#d32f2f',
            cancelButtonText: 'キャンセル'
        });

        if (result.isConfirmed && result.value) {
            const newStartDate = result.value;
            try {
                await setDoc(doc(db, 'settings', 'config'), {
                    seasonStartDate: newStartDate
                }, { merge: true });
                setSeasonStartDate(newStartDate);
                
                await Swal.fire(
                    'リセット完了',
                    `${newStartDate} 以降のデータのみが集計対象になります。<br>(古いデータは削除されず残っています)`,
                    'success'
                );
            } catch(e) {
                console.error(e);
                toast.error("設定の更新に失敗しました");
            }
        }
    };

    // ★ コインの付与・減算は Cloud Functions に任せる。
    //   サーバー側で管理者権限を検証し、users_public も同時に更新される。
    const handleUpdatePoints = async (userId, amount, reason, linkToAccounting, accountingType = 'income') => {
        try {
            await callFn('grantPoints')({
                targetUserId: userId,
                amount: Number(amount),
                reason,
                linkToAccounting: !!linkToAccounting,
                accountingType
            });
            toast.success('更新しました'); fetchUsers();
        } catch(e) {
            console.error('コイン付与エラー:', e?.code || '', e?.message || e);
            toast.error(e?.message || 'エラー');
        }
    };

    const toggleUserRank = async (user, rankId) => {
        const currentRankIds = getUserRankIds(user);
        let newRankIds = [];
  
        if (rankId === 'visitor') {
            newRankIds = ['visitor'];
        } else {
            if (currentRankIds.includes(rankId)) {
                newRankIds = currentRankIds.filter(id => id !== rankId);
            } else {
                newRankIds = [...currentRankIds, rankId];
            }
            if (newRankIds.some(id => id !== 'visitor')) {
                newRankIds = newRankIds.filter(id => id !== 'visitor');
            } else {
                newRankIds = ['visitor'];
            }
        }
        
        const primaryRank = newRankIds.find(id => id !== 'visitor') || 'visitor';

        // ★変更: users と users_public の両方へ書く。
        //
        //   以前は users しか更新していなかった。
        //   チーム一覧（App.js の processTeamMembers）・ランキング・ガチャの
        //   SR/SSR 対象判定は users_public を読むため、管理画面でランクを
        //   変えても、これらの画面には一切反映されなかった。
        //   「設定→公開データ一括同期」を実行した瞬間だけ揃い、
        //   その後に個別変更した人からまたズレていく状態だった。
        //
        //   Admin.js の一括同期と同じ5項目を、同じ値で書き込む。
        const rankFields = {
            rankIds: newRankIds,
            rankId: primaryRank,
            membership: primaryRank,
            isMonthly: newRankIds.includes('monthly'),
            isCollaborator: newRankIds.includes('collaborator')
        };

        try {
            await updateDoc(doc(db, 'users', user.id), rankFields);

            // users_public は存在しない場合もあるため setDoc + merge を使う
            // （updateDoc だとドキュメント未作成のときに失敗する）
            await setDoc(
                doc(db, 'users_public', user.id),
                { ...rankFields, userId: user.id, updatedAt: new Date().toISOString() },
                { merge: true }
            );

            fetchUsers();
        } catch(e) { 
            toast.error('エラー'); 
            console.error(e);
        }
    };
  
    // ★ 運営メンバーのUID一覧を server_secrets/staff に同期する。
    //   Firestoreルールはこのドキュメントを見て管理者判定を行うので、
    //   ルールにUIDを直書きする必要がない。
    //   users.role をルールから直接見ないのは、role を本人が
    //   書き換えられるため（自己申告を信用できない）。
    const syncStaffList = async () => {
        try {
            const snap = await getDocs(collection(db, 'users'));
            const uids = snap.docs
                .filter(d => ['staff', 'admin'].includes(d.data().role))
                .map(d => d.id);
            await setDoc(doc(db, 'server_secrets', 'staff'), {
                uids,
                updatedAt: new Date().toISOString()
            }, { merge: true });
            console.log(`運営リストを同期しました: ${uids.length} 名`, uids);
            return uids.length;
        } catch (e) {
            console.error('運営リストの同期に失敗:', e);
            toast.error('運営権限の反映に失敗しました');
            return null;
        }
    };

    const handleSyncStaff = async () => {
        const n = await syncStaffList();
        if (n !== null) toast.success(`運営メンバー ${n} 名を反映しました`);
    };

    const updateUserField = async (user, field, value) => {
        if (!window.confirm(`変更しますか？`)) return;
        try { 
            if (field === 'status' && value === 'approved') {
                // BAN解除の場合は banUntil もリセットする
                await updateDoc(doc(db, 'users', user.id), { status: 'approved', banUntil: null });
            } else {
                await updateDoc(doc(db, 'users', user.id), { [field]: value }); 
            }
            // ★ role を変えたときは、ルール側の運営リストも更新する
            if (field === 'role') {
                const n = await syncStaffList();
                if (n !== null) toast.success(`運営権限を反映しました（現在 ${n} 名）`);
            }
            fetchUsers(); 
        } catch(e) { toast.error('エラー'); }
    };

    // ★新規追加: 期間を指定してBANを実行する
    const handleBanUser = async (user) => {
        const { value: banType } = await Swal.fire({
            title: '出場停止（BAN）設定',
            html: `<b>${user.displayName}</b> に対するペナルティ期間を選択してください。`,
            input: 'radio',
            inputOptions: {
                '7': '7日間 (1週間)',
                '14': '14日間 (2週間)',
                'permanent': '無期限 BAN'
            },
            inputValidator: (value) => {
                if (!value) return '期間を選択してください';
            },
            showCancelButton: true,
            confirmButtonText: 'BANを実行',
            confirmButtonColor: '#d32f2f'
        });

        if (banType) {
            let banUntil = null;
            if (banType !== 'permanent') {
                const days = parseInt(banType);
                const date = new Date();
                date.setDate(date.getDate() + days);
                banUntil = date.toISOString(); // 解除日を計算して保存
            }
            
            try {
                await updateDoc(doc(db, 'users', user.id), { 
                    status: 'banned', 
                    banUntil: banUntil 
                });
                toast.success('対象ユーザーをBANしました');
                fetchUsers();
            } catch(e) {
                toast.error('エラーが発生しました');
            }
        }
    };

    const handleBulkGrant = async () => {
        if (filteredUsers.length === 0) return alert("対象なし");
        
        const { value: val } = await Swal.fire({
            title: `一括付与 (${filteredUsers.length}名)`,
            html: '<input id="swal-input1" class="swal2-input" placeholder="金額"><input id="swal-input2" class="swal2-input" placeholder="理由"><div style="margin-top:10px"><label><input type="checkbox" id="swal-input3" checked> 支出として記録</label></div>',
            focusConfirm: false, preConfirm: () => [document.getElementById('swal-input1').value, document.getElementById('swal-input2').value, document.getElementById('swal-input3').checked]
        });

        if (!val || !val[0] || !val[1]) return;
        const amt = Number(val[0]); 
        const rsn = val[1]; 
        const isExp = val[2];

        setLoading(true);

        try {
            // ★ 一括付与も Cloud Functions に任せる。
            //   サーバー側で残高マイナスを防ぎ、users_public も同時に揃える。
            const ids = filteredUsers.map(u => u.id);
            const res = await callFn('grantPointsBulk')({
                targetUserIds: ids,
                amount: amt,
                reason: rsn,
                recordAccounting: !!isExp   // ★ 会計記録もサーバー側で行う
            });
            const processedCount = res?.data?.done ?? 0;
            const skipped = res?.data?.skipped ?? 0;
            if (skipped > 0) {
                console.warn('スキップされたユーザー:', skipped, res?.data?.skippedIds);
            }

            // 会計記録は grantPointsBulk の中で行う（途中で通信が切れても片方だけ残らない）

            toast.success(`${processedCount}名への付与が完了しました${skipped > 0 ? `（${skipped}名スキップ）` : ''}`); 
            fetchUsers();
        } catch(e) { 
            console.error(e);
            toast.error("処理中にエラーが発生しました"); 
        } finally {
            setLoading(false);
        }
    };

    // ★修正: BAN中の人数もカウントする
    const userCounts = useMemo(() => {
        const counts = { all: users.length, staff: 0, banned: 0 };
        users.forEach(u => {
            if (u.status === 'banned') counts.banned++;
            if (u.role === 'staff' || u.role === 'admin') counts.staff++;
            const uRankIds = getUserRankIds(u);
            uRankIds.forEach(rid => {
                counts[rid] = (counts[rid] || 0) + 1;
            });
        });
        return counts;
    }, [users]);

    const totalCirculatingPoints = useMemo(() => users.reduce((acc, u) => acc + (u.points || 0), 0), [users]);

    const cooperationUsers = useMemo(() => {
        return users.filter(u => u.idCooperation === true);
    }, [users]);

    // ★修正: BANタブ用のフィルター処理を追加
    const filteredUsers = useMemo(() => {
        let target = users;
        if (userTab === 'banned') {
            target = target.filter(u => u.status === 'banned');
        } else if (userTab === 'staff') {
            target = target.filter(u => u.role === 'staff' || u.role === 'admin');
        } else if (userTab !== 'all') {
            target = target.filter(u => getUserRankIds(u).includes(userTab));
        }
        
        if (!userSearch) return target;
        return target.filter(u => u.displayName && u.displayName.toLowerCase().includes(userSearch.toLowerCase()));
    }, [users, userSearch, userTab]);

    return (
        <div>
            {loading && (
                <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(255,255,255,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999}}>
                    <div style={{fontWeight:'bold', color:'#333'}}>処理中...</div>
                </div>
            )}

            <ProfileModal user={viewingUser} onClose={()=>setViewingUser(null)} pointUnit={pointUnit} refreshData={fetchUsers} ranks={ranks} />
            <PointModal user={pointUser} onClose={()=>setPointUser(null)} onSave={handleUpdatePoints} pointUnit={pointUnit} actionPresets={actionPresets} />

            {cooperationUsers.length > 0 && (
                <div className="card" style={{ border: '2px solid #4caf50', marginBottom: '20px', background: '#f1f8e9' }}>
                    <h3 style={{ margin: '0 0 10px 0', color: '#2e7d32', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        🤝 ID協力 申込者リスト <span style={{ background: '#2e7d32', color: 'white', borderRadius: '10px', padding: '2px 8px', fontSize: '12px' }}>{cooperationUsers.length}名</span>
                    </h3>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {cooperationUsers.map(u => (
                            <div key={`coop-${u.id}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', padding: '10px', borderRadius: '8px', marginBottom: '8px', border: '1px solid #c8e6c9' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {u.pictureUrl ? <img src={u.pictureUrl} style={{width:'30px', height:'30px', borderRadius:'50%', objectFit:'cover'}} alt="" /> : <div style={{width:'30px', height:'30px', borderRadius:'50%', background:'#eee', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px'}}>👤</div>}
                                    <div>
                                        <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#333' }}>
                                            {u.displayName}
                                            <span style={{fontSize: '12px', marginLeft: '3px'}}>
                                                {u.notificationsEnabled && u.fcmToken ? '🔔' : '🔕'}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#666' }}>LINE ID: <span style={{ fontWeight: 'bold', color: u.lineId ? '#333' : '#aaa' }}>{u.lineId || '未入力'}</span></div>
                                    </div>
                                </div>
                                
                                <div style={{ display: 'flex', gap: '5px' }}>
                                    {u.lineId && (
                                        <button
                                            onClick={() => handleCopyLineId(u.lineId)}
                                            style={{ background: '#06c755', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 12px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold' }}
                                        >
                                            IDコピー
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDismissCooperation(u)}
                                        style={{ background: '#9e9e9e', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 12px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold' }}
                                    >
                                        確認済(消去)
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ★追加：運営権限の反映（オーナーのみ表示） */}
            {isOwnerUser && (
            <div className="card" style={{borderLeft:'5px solid #1565c0', marginBottom:'20px', background:'#fff', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'10px'}}>
                <div>
                    <h3 style={{margin:'0 0 5px 0', fontSize:'16px'}}>🛡️ 運営権限の反映</h3>
                    <div style={{fontSize:'12px', color:'#666', lineHeight:'1.5'}}>
                        プルダウンで「運営」に変更すると自動で反映されます。<br/>
                        うまく反映されないときや、手動で作り直したいときに使ってください。
                    </div>
                </div>
                <button 
                    onClick={handleSyncStaff} 
                    className="btn" 
                    style={{background:'#1565c0', color:'white', fontWeight:'bold', fontSize:'12px', padding:'8px 15px', borderRadius:'4px'}}
                >
                    🔄 運営リストを再同期
                </button>
            </div>
            )}

            <div className="card" style={{borderLeft:`5px solid ${COLORS.primary}`, marginBottom:'20px', background:'#fff', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'10px'}}>
                <div>
                    <h3 style={{margin:'0 0 5px 0', fontSize:'16px'}}>🏆 シーズン・ランキング管理</h3>
                    <div style={{fontSize:'12px', color:'#666'}}>
                        現在のシーズン開始日: <b>{seasonStartDate || '未設定 (全期間)'}</b>
                    </div>
                </div>
                <button 
                    onClick={handleResetRanking} 
                    className="btn" 
                    style={{background:'#d32f2f', color:'white', fontWeight:'bold', fontSize:'12px', padding:'8px 15px', borderRadius:'4px'}}
                >
                    🔄 シーズンをリセット
                </button>
            </div>

            <div className="card" style={{background:'#fff3e0', border:'1px solid #ffcc80', textAlign:'center', marginBottom:'20px'}}>
                <div style={{fontSize:'12px', color:'#e65100', fontWeight:'bold'}}>💰 流通ポイント総数</div>
                <div style={{fontSize:'24px', fontWeight:'bold', color:'#333'}}>
                    {formatCurrency(totalCirculatingPoints)} <span style={{fontSize:'14px'}}>{pointUnit}</span>
                </div>
            </div>

            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px'}}>
                <div style={{display:'flex', gap:'5px', overflowX:'auto', paddingBottom:'5px'}}>
                    <button onClick={()=>setUserTab('all')} style={{padding:'6px 12px', borderRadius:'20px', border:'none', background: userTab==='all'?'#333':'#eee', color: userTab==='all'?'white':'#999', fontSize:'12px', whiteSpace:'nowrap', fontWeight:'bold', cursor:'pointer'}}>
                        全員 ({userCounts.all})
                    </button>
                    
                    {ranks.map(r => (
                        <button key={r.id} onClick={()=>setUserTab(r.id)} style={{padding:'6px 12px', borderRadius:'20px', border:'none', background: userTab===r.id?'#333':'#eee', color: userTab===r.id?'white':'#999', fontSize:'12px', whiteSpace:'nowrap', fontWeight:'bold', cursor:'pointer'}}>
                            {r.icon} {r.name} ({userCounts[r.id] || 0})
                        </button>
                    ))}
                    
                    <button onClick={()=>setUserTab('staff')} style={{padding:'6px 12px', borderRadius:'20px', border:'none', background: userTab==='staff'?'#333':'#eee', color: userTab==='staff'?'white':'#999', fontSize:'12px', whiteSpace:'nowrap', fontWeight:'bold', cursor:'pointer'}}>
                        🛡️ 運営 ({userCounts.staff})
                    </button>
                    
                    {/* ★追加: BANタブを追加 */}
                    <button onClick={()=>setUserTab('banned')} style={{padding:'6px 12px', borderRadius:'20px', border:'none', background: userTab==='banned'?'#d32f2f':'#ffebee', color: userTab==='banned'?'white':'#c62828', fontSize:'12px', whiteSpace:'nowrap', fontWeight:'bold', cursor:'pointer'}}>
                        ⛔ BAN ({userCounts.banned})
                    </button>
                </div>
                
                {userTab !== 'all' && userTab !== 'staff' && userTab !== 'banned' && (
                    <button onClick={handleBulkGrant} style={{background:'#e65100', color:'white', border:'none', borderRadius:'20px', padding:'6px 12px', fontSize:'12px', fontWeight:'bold', cursor:'pointer', whiteSpace:'nowrap'}}>
                        🎁 一括付与
                    </button>
                )}
            </div>

            <input type="text" placeholder="🔍 名前検索" value={userSearch} onChange={e=>setUserSearch(e.target.value)} />
            
            <div style={{display:'grid', gap:'10px'}}>
                {filteredUsers.slice(0, userLimit).map(u => {
                    const userRankIds = getUserRankIds(u);
                    
                    // ★追加: BAN中の表示テキスト作成
                    let statusText = u.status === 'approved' ? '承認済' : (u.status === 'pending' ? '⏳ 承認待ち' : u.status);
                    if (u.status === 'banned') {
                        statusText = `⛔ BAN中 ${u.banUntil ? `(~${new Date(u.banUntil).toLocaleDateString()})` : '(無期限)'}`;
                    }

                    return (
                    <div key={u.id} className="card" style={{borderLeft:`4px solid ${u.status === 'banned' ? '#d32f2f' : (u.role==='staff'?COLORS.primary: (userRankIds.includes('monthly')?COLORS.secondary:'#ccc'))}`, padding:'12px', display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0', opacity: u.status==='banned'?0.6:1}}>
                        <div style={{display:'flex', alignItems:'center'}}>
                            {u.pictureUrl ? <img src={u.pictureUrl} style={{width:'40px', height:'40px', borderRadius:'50%', marginRight:'10px'}} alt="" /> : <div style={{width:'40px', height:'40px', borderRadius:'50%', background:'#eee', marginRight:'10px'}}></div>}
                            <div>
                                <div style={{fontWeight:'bold'}}>
                                    {u.displayName} {u.role==='staff' && '🛡️'}
                                    <span style={{fontSize: '12px', marginLeft: '5px'}}>
                                        {u.notificationsEnabled && u.fcmToken ? '🔔' : '🔕'}
                                    </span>
                                </div>
                                <div style={{fontSize:'10px', color: u.status === 'banned' ? '#d32f2f' : '#999', fontWeight: u.status === 'banned' ? 'bold' : 'normal'}}>
                                    {statusText}
                                    {u.lastLogin && <span style={{marginLeft:'5px', color:'#999', fontWeight:'normal'}}>🕒 {new Date(u.lastLogin).toLocaleDateString()}</span>}
                                </div>
                                <div style={{display:'flex', gap:'10px', marginTop:'5px'}}>
                                    <button onClick={()=>setViewingUser(u)} style={{background:'none', border:'none', color:COLORS.primary, fontSize:'11px', textDecoration:'underline', cursor:'pointer', padding:0}}>📄 詳細</button>
                                    <button onClick={()=>setPointUser(u)} style={{background:'none', border:'none', color:'#e65100', fontSize:'11px', textDecoration:'underline', cursor:'pointer', padding:0, fontWeight:'bold'}}><img src="/rbcoin.png" style={{width:'12px', height:'12px', verticalAlign:'middle'}} alt="" /> {formatCurrency(u.points||0)}</button>
                                </div>
                            </div>
                        </div>
                        
                        <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'5px'}}>
                            <div style={{display:'flex', flexWrap:'wrap', gap:'3px', justifyContent:'flex-end', maxWidth:'150px'}}>
                                {ranks.map(r => {
                                    const isActive = userRankIds.includes(r.id);
                                    return (
                                        <button 
                                            key={r.id} 
                                            onClick={()=>toggleUserRank(u, r.id)} 
                                            style={{
                                                fontSize:'9px', padding:'3px 6px', borderRadius:'10px', 
                                                border: isActive ? `1px solid ${COLORS.success}` : '1px solid #ddd', 
                                                background: isActive ? '#e8f5e9' : 'white', 
                                                color: isActive ? '#2e7d32' : '#999', 
                                                cursor:'pointer'
                                            }}
                                        >
                                            {r.icon} {r.name}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* ★ 役割の変更はオーナーのみ。他の運営には現在の役割を表示だけする */}
                            {isOwnerUser ? (
                                <select value={u.role||'member'} onChange={(e)=>updateUserField(u, 'role', e.target.value)} style={{fontSize:'10px', padding:'2px', background: u.role==='staff'?'#e3f2fd':'white', width:'70px', height:'auto', margin:0}}>
                                    <option value="member">一般</option><option value="staff">運営</option>
                                </select>
                            ) : (
                                <span style={{fontSize:'10px', padding:'2px 6px', borderRadius:'3px', width:'70px', textAlign:'center', background: u.role==='staff'?'#e3f2fd':'#f5f5f5', color:'#666'}}>
                                    {u.role==='staff' ? '運営' : '一般'}
                                </span>
                            )}
                            
                            {u.status==='pending' && <button onClick={()=>updateUserField(u,'status','approved')} style={{fontSize:'10px', background:COLORS.success, color:'white', border:'none', borderRadius:'3px', padding:'2px 5px'}}>承認する</button>}
                            
                            {/* ★変更: BANの実行/解除ボタン */}
                            {u.status === 'banned' ? (
                                <button onClick={()=>updateUserField(u,'status','approved')} style={{fontSize:'10px', background:'#e57373', color:'white', border:'none', borderRadius:'3px', padding:'4px 8px', fontWeight:'bold', cursor:'pointer'}}>BAN解除</button>
                            ) : (
                                <button onClick={()=>handleBanUser(u)} style={{fontSize:'10px', background:'#666', color:'white', border:'none', borderRadius:'3px', padding:'4px 8px', fontWeight:'bold', cursor:'pointer'}}>BAN</button>
                            )}
                        </div>
                    </div>
                    );
                })}
                
                {filteredUsers.length > userLimit && (
                    <button onClick={()=>setUserLimit(prev=>prev + 50)} className="btn" style={{background:'#eee', color:'#666', marginTop:'10px'}}>
                        ▼ もっと見る ({filteredUsers.length - userLimit}件)
                    </button>
                )}
            </div>
        </div>
    );
};

export default AdminUsers;